import React, { useState } from 'react';
import { CheckCircle, XCircle, RotateCcw, ChevronDown, ChevronUp, Brain, Info } from 'lucide-react';

const DEMO_QUESTIONS = [
  {
    id: 549,
    question_text: "**Worauf müssen Sie als Schiffsführer vor Reiseantritt hinsichtlich der **Seekarten** und **Seebücher** achten?**",
    answer_text: "Auf **Vollständigkeit der Unterlagen** und deren **Berichtigung auf den neuesten Stand**.",
    explanation_text: "Die Seekarten und Seebücher sind wichtige Navigationshilfsmittel, die Schiffsführern beim sicheren Steuern eines Schiffs helfen. Vor Reiseantritt müssen diese Dokumente vollständig sein und aktualisiert werden."
  },
  {
    id: 2,
    question_text: "Warum muss in der **GPS-Navigation** das jeweilige **Kartendatum** unbedingt berücksichtigt werden?",
    answer_text: "Weil sich das von **GPS** verwendete **Bezugssystem** **WGS 84** (**World Geodetic System 1984**) von anderen verwendeten Bezugssystemen (**Kartendatum**) unterscheiden kann.",
    explanation_text: "Das Kartendatum bestimmt, wie Punkte auf der Erde auf der Karte dargestellt werden. Das WGS 84 ist ein globales Standard-Kartendatum, das alle GPS-Empfängersysteme verwenden."
  }
];

const DemoQuiz = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [showExplanation, setShowExplanation] = useState(false);

  const currentQuestion = DEMO_QUESTIONS[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === DEMO_QUESTIONS.length - 1;

  const handleShowAnswer = () => {
    setShowAnswer(true);
  };

  const handleHideAnswer = () => {
    setShowAnswer(false);
    setShowExplanation(false);
  };

  const handleNextQuestion = (isCorrect) => {
    setAnswers([...answers, isCorrect]);
    if (!isLastQuestion) {
      setCurrentQuestionIndex(prev => prev + 1);
      setShowAnswer(false);
      setShowExplanation(false);
    }
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setShowAnswer(false);
    setAnswers([]);
    setShowExplanation(false);
  };

  const toggleExplanation = () => {
    setShowExplanation(!showExplanation);
  };

  const formatText = (text) => {
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  };

  if (answers.length === DEMO_QUESTIONS.length) {
    return (
      <div className="card bg-base-100 shadow-xl border-4 border-primary">
        <div className="card-body">
          <div>
            <h3 className="text-2xl font-bold text-center mb-4">Quiz abgeschlossen!</h3>
            <div className="text-center mb-6">
              <div className="stats shadow mb-4">
                <div className="stat">
                  <div className="stat-title">Richtige Antworten</div>
                  <div className="stat-value text-success">{answers.filter(a => a).length}</div>
                  <div className="stat-desc">von {DEMO_QUESTIONS.length} Fragen</div>
                </div>
              </div>
              <div className="flex justify-center gap-2 mb-6">
                {answers.map((isCorrect, index) => (
                  <span 
                    key={index} 
                    className={`w-4 h-4 rounded-full ${isCorrect ? 'bg-success' : 'bg-error'}`}
                  />
                ))}
              </div>
              
              <div className="alert alert-success mb-6">
                <Info className="w-5 h-5" />
                <div>
                  <h4 className="font-bold">Dein Lernfortschritt wird gespeichert</h4>
                  <p className="text-sm">Unser System ermittelt den perfekten Zeitpunkt für deine nächste Wiederholung.</p>
                </div>
              </div>

              <div className="text-center">
                <button 
                  onClick={handleRestart} 
                  className="btn btn-primary gap-2 hover:scale-105 transition-transform"
                >
                  <RotateCcw className="w-4 h-4" />
                  Nochmal versuchen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-xl border-4 border-primary">
      <div className="card-body">
        <div className="flex justify-between items-center mb-4">
          <div className="badge text-white badge-primary">Demo-Frage {currentQuestionIndex + 1}/{DEMO_QUESTIONS.length}</div>
          <div className="flex gap-2">
            {answers.map((isCorrect, index) => (
              <span 
                key={index} 
                className={`w-3 h-3 rounded-full ${isCorrect ? 'bg-success' : 'bg-error'}`}
              />
            ))}
          </div>
        </div>

        <div className="prose max-w-none mb-6">
          <div 
            dangerouslySetInnerHTML={{ 
              __html: formatText(currentQuestion.question_text)
            }}
          />
        </div>

        {!showAnswer ? (
          <button 
            onClick={handleShowAnswer}
            className="btn btn-success w-full gap-2 hover:scale-105 transition-transform animate-pulse"
          >
            Antwort anzeigen
            <ChevronDown className="w-4 h-4" />
          </button>
        ) : (
          <div>
            <div className="prose max-w-none mb-6 p-4 bg-base-200 rounded-lg">
              <div
                dangerouslySetInnerHTML={{
                  __html: formatText(currentQuestion.answer_text)
                }}
              />
              <button 
                onClick={handleHideAnswer}
                className="btn btn-ghost btn-sm mt-4 w-full hover:bg-base-300 transition-colors"
              >
                <ChevronUp className="w-4 h-4" />
                Antwort ausblenden
              </button>
            </div>

            <div className="flex gap-4 mb-4">
              <button 
                onClick={() => handleNextQuestion(false)}
                className="btn btn-error flex-1 gap-2 hover:scale-105 transition-transform"
              >
                <XCircle className="w-5 h-5" />
                Falsch
              </button>
              <button 
                onClick={() => handleNextQuestion(true)}
                className="btn btn-success flex-1 gap-2 hover:scale-105 transition-transform"
              >
                <CheckCircle className="w-5 h-5" />
                Richtig
              </button>
            </div>

            <button
              onClick={toggleExplanation}
              className="btn btn-ghost w-full gap-2 hover:bg-base-200 transition-colors"
            >
              <Brain className="w-5 h-5 text-primary" />
              {showExplanation ? 'Erklärung ausblenden' : 'Erklärung anzeigen'}
            </button>

            {showExplanation && (
              <div className="mt-4 transition-all">
                <div className="p-4 bg-base-200 rounded-lg prose max-w-none">
                  <p>{currentQuestion.explanation_text}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoQuiz; 